import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import classNames from 'classnames'
import { type ReactNode, useRef } from 'react'
import { createPortal } from 'react-dom'

import AtomTableHeader from '@/components/Atoms/AtomTableHeader/AtomTableHeader'
import { TABLE_EMPTY_TEXT } from '@/constants/ui'
import { createSafeRowMouseUp } from '@/hooks/ui/createSafeRowMouseUp'
import { getGridTemplateColumns } from '@/utils/table/getGridTemplateColumns'

import cn from './AtomVirtualizedTable.module.scss'
import { useFloatingTableHeader } from './hooks/useFloatingTableHeader'
import LoadMoreTrigger from './LoadMoreTrigger/LoadMoreTrigger'

const DEFAULT_ROW_HEIGHT = 52
const ROWS_OVERSCAN = 10

export type AtomVirtualizedTableInfiniteScroll = {
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => void
}

type AtomVirtualizedTableProps<TData> = {
  data: TData[]
  columns: ColumnDef<TData, unknown>[]
  getRowId: (row: TData) => string
  infiniteScroll?: AtomVirtualizedTableInfiniteScroll
  columnWidths?: Partial<Record<string, string>>
  estimateRowHeight?: number
  isOrganisationSelected?: boolean
  isStale?: boolean
  emptyContent?: ReactNode
  getRowClassName?: (row: TData) => string | undefined
  onRowClick?: (row: TData) => void
  hasFloatingHeader?: boolean
  ariaLabel?: string
  className?: string
}

function AtomVirtualizedTable<TData>({
  data,
  columns,
  getRowId,
  infiniteScroll,
  columnWidths = {},
  estimateRowHeight = DEFAULT_ROW_HEIGHT,
  isOrganisationSelected = false,
  isStale = false,
  emptyContent,
  getRowClassName,
  onRowClick,
  hasFloatingHeader = false,
  ariaLabel,
  className,
}: AtomVirtualizedTableProps<TData>) {
  const listRef = useRef<HTMLDivElement>(null)
  const frameRef = useRef<HTMLDivElement>(null)
  const headerAnchorRef = useRef<HTMLDivElement>(null)
  const floatingHeaderRef = useRef<HTMLDivElement>(null)
  const floatingHeaderScrollRef = useRef<HTMLDivElement>(null)

  useFloatingTableHeader({
    enabled: hasFloatingHeader && data.length > 0,
    anchorRef: headerAnchorRef,
    frameRef,
    floatingRef: floatingHeaderRef,
    floatingScrollRef: floatingHeaderScrollRef,
    visibleClassName: cn.floatingHeaderVisible,
    hiddenClassName: cn.floatingHeaderHidden,
  })

  const table = useReactTable({
    data,
    columns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
  })
  const rows = table.getRowModel().rows

  const rowVirtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => estimateRowHeight,
    overscan: ROWS_OVERSCAN,
    scrollMargin: listRef.current
      ? listRef.current.getBoundingClientRect().top + window.scrollY
      : 0,
    measureElement: (element) => element.getBoundingClientRect().height,
  })

  const gridColumns = getGridTemplateColumns(
    table.getVisibleLeafColumns().map((column) => column.id),
    columnWidths
  )

  if (rows.length === 0) {
    return (
      <div role="status" className={classNames(cn.empty, className)}>
        {emptyContent ?? TABLE_EMPTY_TEXT}
      </div>
    )
  }

  return (
    <div
      className={classNames(
        cn.container,
        isOrganisationSelected && cn.organisationSelected,
        className
      )}
    >
      <div ref={frameRef} className={cn.table}>
        <div
          role="table"
          aria-label={ariaLabel}
          aria-rowcount={rows.length}
          className={classNames(cn.grid, isStale && cn.stale)}
          style={{ '--grid-columns': gridColumns }}
        >
          <div ref={headerAnchorRef}>
            <AtomTableHeader table={table} />
          </div>

          <div
            ref={listRef}
            role="rowgroup"
            className={cn.virtualList}
            style={{
              '--virtual-total-size': `${rowVirtualizer.getTotalSize()}px`,
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index]
              return (
                <div
                  key={row.id}
                  role="row"
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  className={classNames(
                    cn.row,
                    onRowClick && cn.clickableRow,
                    getRowClassName?.(row.original)
                  )}
                  onMouseUp={
                    onRowClick
                      ? createSafeRowMouseUp(() => onRowClick(row.original))
                      : undefined
                  }
                  style={{
                    '--virtual-row-start': `${virtualRow.start - rowVirtualizer.options.scrollMargin}px`,
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <div key={cell.id} role="cell" className={cn.cell}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {hasFloatingHeader &&
        createPortal(
          <div
            ref={floatingHeaderRef}
            aria-hidden
            className={classNames(
              cn.floatingHeader,
              isOrganisationSelected && cn.organisationSelected
            )}
          >
            <div
              ref={floatingHeaderScrollRef}
              className={cn.floatingHeaderFrame}
            >
              <div
                className={cn.floatingHeaderGrid}
                style={{ '--grid-columns': gridColumns }}
              >
                <AtomTableHeader table={table} />
              </div>
            </div>
          </div>,
          document.body
        )}

      {infiniteScroll && (
        <LoadMoreTrigger
          hasNextPage={infiniteScroll.hasNextPage}
          isFetchingNextPage={infiniteScroll.isFetchingNextPage}
          onLoadMore={infiniteScroll.onLoadMore}
        />
      )}
    </div>
  )
}

export default AtomVirtualizedTable
